import type { ArtStateValues } from '../core/ArtState.js';
import type { InteractionMode } from '../core/InteractionMode.js';
import { isInteractionMode } from '../core/InteractionMode.js';
import {
  VISUAL_RECIPE_VERSION,
  parseRecipe,
  type RenderMode,
  type VisualRecipe,
  isRenderMode,
} from '../recipe/VisualRecipe.js';

/**
 * A performance, written down.
 *
 * The decision this file exists to enforce: **a replay stores what was done, not
 * what was drawn.** No mutation list, no particle positions, no geometry, no
 * frames. Those are all consequences, and storing a consequence would freeze the
 * one thing Phase 5 needs to vary — a recording that contained its own visuals
 * could only ever be played back as itself.
 *
 * What is stored is the performance: which song, what the player did and when,
 * what the game decided, and the seed everything downstream was generated from.
 * Feed that back through the same signal bus a live run uses and the visuals are
 * *regenerated*, which means they can be regenerated differently.
 *
 * The size difference is not incidental. A three-minute run is a few hundred
 * inputs and a few hundred events — tens of kilobytes of JSON — where a mutation
 * log would be tens of thousands of entries and a frame dump would be gigabytes.
 */

export const REPLAY_SCHEMA_VERSION = 1;

/** A press. The primary record; everything else can be checked against it. */
export interface ReplayInput {
  /** Song time, seconds. */
  readonly time: number;
  /**
   * What kind of press this was.
   *
   * `note` judged something; `offBeat` did not. Both are kept — an off-beat tap
   * is invisible to the score and is exactly the material ART mode works with,
   * so a replay that dropped it would not be a recording of the performance.
   */
  readonly inputType: 'note' | 'offBeat';
  readonly phase: 'press' | 'release';
  /** World position of the tap. Mandalizm has no lanes; this is its analogue. */
  readonly x: number;
  readonly y: number;
  readonly strength: number;
  readonly judged: boolean;
  /** Seconds from the nearest beat, which is what "off-beat" is measured by. */
  readonly beatDistance: number;
}

/**
 * What the game decided.
 *
 * Secondary by design: with the chart and the input timeline these are
 * derivable, and §4 asks for the primary/verification split to be explicit. They
 * are stored anyway because they make a replay checkable without re-running the
 * simulation, and because they carry the running score the visual levels need.
 */
export interface ReplayRhythmEvent {
  readonly time: number;
  readonly type:
    | 'NOTE_HIT'
    | 'NOTE_MISS'
    | 'COMBO_BREAK'
    | 'BAR'
    | 'SECTION'
    | 'SONG_START'
    | 'SONG_END';
  readonly grade?: string;
  readonly offsetMs?: number;
  readonly eventIndex?: number;
  readonly combo?: number;
  /** Running totals at this moment, so replay can restore the signal levels. */
  readonly score?: number;
  readonly accuracy?: number;
}

export interface ReplaySong {
  readonly songId: string;
  readonly packId: string;
  readonly displayName: string;
  readonly difficulty: string;
  readonly bpm: number;
  readonly durationSeconds: number;
  readonly waveSpeed: number;
  /**
   * Identifies the beat grid without storing it.
   *
   * A cheap hash over the detected beat times plus the event count. Two packs
   * that hash the same will replay the same; a mismatch means the pack changed
   * under the recording, which is worth warning about rather than guessing at.
   */
  readonly chartHash: string;
  readonly eventCount: number;
}

export interface ReplayPerformance {
  readonly interactionMode: InteractionMode;
  readonly renderMode: RenderMode;
  readonly visualSeed: number;
  readonly originalRecipe: VisualRecipe;
}

export interface ReplayResult {
  readonly score: number;
  readonly accuracy: number;
  readonly hitCount: number;
  readonly missCount: number;
  readonly maxCombo: number;
  readonly totalEvents: number;
}

export interface ReplayMetadata {
  readonly createdAt: number;
  readonly engineVersion: string;
  /**
   * The artwork's condition when the run ended.
   *
   * **Not the source of truth.** `ArtState` is rebuilt from the timeline on
   * every replay; this is kept only so a replay can be checked against the
   * performance it came from, which is what §14's validation compares.
   */
  readonly finalArtState?: ArtStateValues;
  readonly signalCounts?: Readonly<Record<string, number>>;
  readonly mutationCount?: number;
}

export interface ReplayData {
  readonly version: number;
  readonly song: ReplaySong;
  readonly performance: ReplayPerformance;
  readonly timeline: {
    readonly playerInputs: readonly ReplayInput[];
    readonly rhythmEvents: readonly ReplayRhythmEvent[];
  };
  readonly result: ReplayResult;
  readonly metadata: ReplayMetadata;
}

// ---------------------------------------------------------------------------

export type ReplayCompatibility = 'ok' | 'migrated' | 'incompatible';

export interface ReplayParseResult {
  readonly replay: ReplayData | null;
  readonly compatibility: ReplayCompatibility;
  readonly problems: readonly string[];
}

/**
 * Reads a replay from untrusted JSON.
 *
 * Three outcomes rather than two, because §15 asks for the distinction: a file
 * may be fine, may be an older shape that this build can fill in, or may be
 * something this build cannot honestly load. A future version is always the
 * third — a newer writer may mean fields differently, and quietly reading it
 * with today's rules would produce a plausible, wrong performance.
 */
export function parseReplay(input: unknown): ReplayParseResult {
  const problems: string[] = [];
  if (typeof input !== 'object' || input === null) {
    return { replay: null, compatibility: 'incompatible', problems: ['not an object'] };
  }
  const raw = input as Record<string, unknown>;

  const version = typeof raw.version === 'number' ? raw.version : 0;
  if (version > REPLAY_SCHEMA_VERSION) {
    return {
      replay: null,
      compatibility: 'incompatible',
      problems: [`replay version ${version} is newer than this build understands`],
    };
  }
  if (version <= 0) {
    return { replay: null, compatibility: 'incompatible', problems: ['missing version'] };
  }

  const song = parseSong(raw.song, problems);
  if (song === null) {
    return { replay: null, compatibility: 'incompatible', problems };
  }

  const performance = parsePerformance(raw.performance, problems);
  const timeline = raw.timeline as Record<string, unknown> | undefined;
  const playerInputs = parseInputs(timeline?.playerInputs, problems);
  const rhythmEvents = parseRhythmEvents(timeline?.rhythmEvents, problems);

  if (playerInputs.length === 0 && rhythmEvents.length === 0) {
    problems.push('timeline is empty; nothing to replay');
  }

  const replay: ReplayData = {
    version: REPLAY_SCHEMA_VERSION,
    song,
    performance,
    timeline: { playerInputs, rhythmEvents },
    result: parseResult(raw.result),
    metadata: parseMetadata(raw.metadata),
  };

  return {
    replay,
    compatibility: version < REPLAY_SCHEMA_VERSION || problems.length > 0 ? 'migrated' : 'ok',
    problems,
  };
}

export function serializeReplay(replay: ReplayData): string {
  return JSON.stringify(replay, null, 2);
}

/**
 * Identifies a chart without storing it.
 *
 * FNV-1a over the beat grid and the event times. Deliberately cheap and
 * deliberately not cryptographic: the question it answers is "is this the same
 * chart I recorded against", and a collision costs a warning, not a security
 * boundary.
 */
export function chartHash(beatTimes: ArrayLike<number>, eventTimes: ArrayLike<number>): string {
  let hash = 0x811c9dc5;
  const mix = (value: number): void => {
    // Quantised to a millisecond so floating-point noise does not change the
    // hash of a chart that is, musically, identical.
    const quantised = Math.round(value * 1000) | 0;
    hash ^= quantised;
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) | 0;
  };
  mix(beatTimes.length);
  for (let index = 0; index < beatTimes.length; index += 1) mix(beatTimes[index] as number);
  mix(eventTimes.length);
  for (let index = 0; index < eventTimes.length; index += 1) mix(eventTimes[index] as number);
  return (hash >>> 0).toString(16).padStart(8, '0');
}

// ---------------------------------------------------------------------------

function parseSong(value: unknown, problems: string[]): ReplaySong | null {
  if (typeof value !== 'object' || value === null) {
    problems.push('missing song');
    return null;
  }
  const raw = value as Record<string, unknown>;
  const packId = text(raw.packId, '');
  if (packId.length === 0) {
    problems.push('song has no packId; the replay cannot be matched to a song');
    return null;
  }
  return {
    songId: text(raw.songId, packId),
    packId,
    displayName: text(raw.displayName, packId),
    difficulty: text(raw.difficulty, 'normal'),
    bpm: number(raw.bpm, 0),
    durationSeconds: number(raw.durationSeconds, 0),
    waveSpeed: number(raw.waveSpeed, 1),
    chartHash: text(raw.chartHash, ''),
    eventCount: number(raw.eventCount, 0),
  };
}

function parsePerformance(value: unknown, problems: string[]): ReplayPerformance {
  const raw = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
  const mode = text(raw.interactionMode, 'SCORE');
  const render = text(raw.renderMode, 'FIXED');
  const parsedRecipe = parseRecipe(raw.originalRecipe);
  if (parsedRecipe.recipe === null) {
    problems.push('original recipe could not be read; replay will need one supplied');
  }
  const seed = number(raw.visualSeed, parsedRecipe.recipe?.seed ?? 0) | 0;
  return {
    interactionMode: isInteractionMode(mode) ? mode : 'SCORE',
    renderMode: isRenderMode(render) ? render : 'FIXED',
    visualSeed: seed,
    originalRecipe: parsedRecipe.recipe ?? {
      version: VISUAL_RECIPE_VERSION,
      seed,
      renderMode: 'FIXED',
      layers: [],
    },
  };
}

function parseInputs(value: unknown, problems: string[]): ReplayInput[] {
  if (!Array.isArray(value)) {
    if (value !== undefined) problems.push('playerInputs is not a list');
    return [];
  }
  const out: ReplayInput[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) continue;
    const raw = entry as Record<string, unknown>;
    const time = number(raw.time, Number.NaN);
    if (!Number.isFinite(time)) continue;
    const type = text(raw.inputType, 'note');
    out.push({
      time,
      inputType: type === 'offBeat' ? 'offBeat' : 'note',
      phase: text(raw.phase, 'press') === 'release' ? 'release' : 'press',
      x: number(raw.x, 0),
      y: number(raw.y, 0),
      strength: number(raw.strength, 1),
      judged: raw.judged === true,
      beatDistance: number(raw.beatDistance, 0),
    });
  }
  // Sorted defensively: a hand-edited file, or one merged from two sources,
  // must not make the player emit events out of order.
  out.sort((a, b) => a.time - b.time);
  return out;
}

const RHYTHM_TYPES = new Set([
  'NOTE_HIT',
  'NOTE_MISS',
  'COMBO_BREAK',
  'BAR',
  'SECTION',
  'SONG_START',
  'SONG_END',
]);

function parseRhythmEvents(value: unknown, problems: string[]): ReplayRhythmEvent[] {
  if (!Array.isArray(value)) {
    if (value !== undefined) problems.push('rhythmEvents is not a list');
    return [];
  }
  const out: ReplayRhythmEvent[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) continue;
    const raw = entry as Record<string, unknown>;
    const time = number(raw.time, Number.NaN);
    const type = text(raw.type, '');
    if (!Number.isFinite(time) || !RHYTHM_TYPES.has(type)) continue;
    out.push({
      time,
      type: type as ReplayRhythmEvent['type'],
      ...(typeof raw.grade === 'string' ? { grade: raw.grade } : {}),
      ...(typeof raw.offsetMs === 'number' ? { offsetMs: raw.offsetMs } : {}),
      ...(typeof raw.eventIndex === 'number' ? { eventIndex: raw.eventIndex } : {}),
      ...(typeof raw.combo === 'number' ? { combo: raw.combo } : {}),
      ...(typeof raw.score === 'number' ? { score: raw.score } : {}),
      ...(typeof raw.accuracy === 'number' ? { accuracy: raw.accuracy } : {}),
    });
  }
  out.sort((a, b) => a.time - b.time);
  return out;
}

function parseResult(value: unknown): ReplayResult {
  const raw = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
  return {
    score: number(raw.score, 0),
    accuracy: number(raw.accuracy, 0),
    hitCount: number(raw.hitCount, 0),
    missCount: number(raw.missCount, 0),
    maxCombo: number(raw.maxCombo, 0),
    totalEvents: number(raw.totalEvents, 0),
  };
}

function parseMetadata(value: unknown): ReplayMetadata {
  const raw = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
  const art = raw.finalArtState;
  return {
    createdAt: number(raw.createdAt, 0),
    engineVersion: text(raw.engineVersion, 'unknown'),
    ...(typeof art === 'object' && art !== null
      ? { finalArtState: art as ArtStateValues }
      : {}),
    ...(typeof raw.signalCounts === 'object' && raw.signalCounts !== null
      ? { signalCounts: raw.signalCounts as Record<string, number> }
      : {}),
    ...(typeof raw.mutationCount === 'number' ? { mutationCount: raw.mutationCount } : {}),
  };
}

function number(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function text(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}
